import { useState, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import './App.css';

const WEB3FORMS_KEY = '3c121b2d-7193-4bc3-919f-4998dca9bef6';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    containerType: '',
    storageType: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const turnstileRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isFormReady = formData.name.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.containerType !== '' &&
    formData.storageType !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!turnstileToken) {
      setSubmitError('Please complete the verification check before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `New Enquiry from ${formData.name} — Cotswold Storage`,
        from_name: 'Cotswold Storage Website',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        container_size: formData.containerType,
        storage_type: formData.storageType,
        message: formData.message || 'No additional details provided',
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          containerType: '',
          storageType: '',
          message: ''
        });
        setTurnstileToken(null);
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setSubmitError('Something went wrong. Please try again or call us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('Something went wrong. Please try again or call us directly on 01451 822220.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <div className="logo">
            <img src="/images/CostwoldStorageLogo.png" alt="Cotswold Storage" className="logo-image" />
          </div>
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><button onClick={() => scrollToSection('containers')}>Containers</button></li>
            <li><button onClick={() => scrollToSection('benefits')}>How It Works</button></li>
            <li><button onClick={() => scrollToSection('why-choose')}>Why Choose Us</button></li>
            <li><button onClick={() => scrollToSection('ideal-for')}>Ideal For</button></li>
            <li><button onClick={() => scrollToSection('location')}>Location</button></li>
            <li><button onClick={() => scrollToSection('contact')}>Contact Us</button></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1>Secure Self Storage in the Heart of the Cotswolds</h1>
            <p className="subtitle">
              Brand new, weatherproof shipping containers on a large, owner-managed site
              near Bourton-on-the-Water. Personal service, high-tech security, and
              flexible terms.
            </p>
            <ul className="hero-features">
              <li>20ft Containers from £220/month + VAT</li>
              <li>Brand New &amp; Fully Weatherproof</li>
              <li>ANPR &amp; Modern CCTV Security</li>
              <li>Heavy-Duty Padlock Included</li>
              <li>Owners On-Site</li>
            </ul>
            <p className="hero-phone">
              Call <a href="tel:01451822220" className="hero-phone-link">01451 822220</a> or use the form below to arrange a site visit.
            </p>
            <button className="cta-button" onClick={() => scrollToSection('contact')}>
              Book a Site Visit
              <span>→</span>
            </button>
          </div>
          <div className="hero-image">
            <img src="/images/eco-self-storage.jpg" alt="Cotswold Storage containers" />
          </div>
        </div>
      </section>

      {/* Container Options */}
      <section id="containers">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Choose Your Container</h2>
            <p className="section-subtitle">
              Brand new, weatherproof containers with heavy-duty padlock included as standard
            </p>
          </div>

          <div className="containers-grid">
            <div className="container-card">
              <div className="container-icon"></div>
              <h3>20ft</h3>
              <div className="container-price">From £220<span>/month + VAT</span></div>
              <div className="size">20' x 8' x 8.5'</div>
              <div className="capacity">~1,165 cubic feet</div>
              <h4 className="what-fits-title">What Fits:</h4>
              <ul className="features-list">
                <li>Furniture from a 2–3 bedroom house</li>
                <li>Contents of a medium office</li>
                <li>100–150 moving boxes</li>
                <li>Large household appliances</li>
                <li>Classic car or vehicle storage</li>
              </ul>
            </div>

            <div className="container-card coming-soon">
              <div className="container-icon"></div>
              <h3>40ft</h3>
              <div className="container-price muted">Price TBC</div>
              <div className="size">40' x 8' x 8.5'</div>
              <div className="coming-soon-badge">Coming Soon</div>
              <ul className="features-list">
                <li>Maximum capacity — ~2,385 cubic feet</li>
                <li>Commercial stock &amp; inventory</li>
                <li>Large equipment storage</li>
                <li>Multiple vehicles</li>
                <li>Ideal for growing businesses</li>
              </ul>
            </div>
          </div>

          {/* Rental Terms Banner */}
          <div className="rental-terms-banner">
            <div className="term-item">
              <span className="term-icon icon-calendar-term"></span>
              <span>3 Month Minimum Rental</span>
            </div>
            <div className="term-divider"></div>
            <div className="term-item">
              <span className="term-icon icon-pound-term"></span>
              <span>First Month Upfront</span>
            </div>
            <div className="term-divider"></div>
            <div className="term-item">
              <span className="term-icon icon-repeat-term"></span>
              <span>Standing Order for Ongoing Payments</span>
            </div>
          </div>

          <div className="lighting-note">
            <span className="lighting-icon icon-bulb"></span>
            <p>
              <strong>Optional Lighting Packages Available</strong> — Rechargeable magnetic interior lights
              can be supplied for your container. Ask us for details when you book your site visit.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="benefits">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              We like to make sure everything's right before you commit
            </p>
          </div>

          <div className="how-it-works-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Book a Site Visit</h3>
              <p>
                <a href="#contact" className="contact-link" style={{ textDecoration: 'underline' }} onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Get in touch</a> and we'll arrange a show-around so you can see the site and
                containers for yourself. We want to make sure you're completely happy before
                moving forward.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Sign Your Agreement</h3>
              <p>
                Once you're happy, we'll get a rental contract signed with a form of ID.
                The first month's payment is taken upfront, then a standing order or direct
                debit is set up for ongoing payments.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Move In &amp; Store</h3>
              <p>
                Collect your heavy-duty padlock, drive up to your container, and start
                storing. Access the site during opening hours and enjoy the peace of mind
                that comes with on-site management and 24/7 CCTV.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-choose">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Cotswold Storage</h2>
            <p className="section-subtitle">
              A personal, secure, and high-quality storage experience
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon icon-security"></div>
              <h3>High-Tech Security</h3>
              <p>
                Our site is covered by ANPR and modern CCTV cameras, providing
                round-the-clock surveillance and monitoring for complete peace of mind.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon icon-onsite"></div>
              <h3>Owner On-Site</h3>
              <p>
                We're on-site, so there's always someone here to say hello, answer
                questions, or lend a hand. Our customers love the personal touch.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon icon-newcontainer"></div>
              <h3>Brand New Containers</h3>
              <p>
                All our containers are brand new and fully weatherproof. Each one comes
                supplied with a heavy-duty padlock so your belongings are protected from
                day one.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon icon-access"></div>
              <h3>Large Access Site</h3>
              <p>
                Our spacious site offers easy drive-up access for vehicles of all sizes.
                HGV and removal company deliveries are welcome with 24 hours' notice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section id="ideal-for">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">What Our Storage Is Ideal For</h2>
            <p className="section-subtitle">
              Whether it's personal belongings or business stock, we've got you covered
            </p>
          </div>

          <div className="ideal-for-grid">
            <div className="ideal-card">
              <div className="ideal-icon icon-home"></div>
              <h3>Home &amp; Household</h3>
              <p>
                Decluttering, renovating, or between moves? Store your furniture,
                appliances, and personal items in a secure, weatherproof container.
              </p>
            </div>

            <div className="ideal-card">
              <div className="ideal-icon icon-car"></div>
              <h3>Classic Car Storage</h3>
              <p>
                Keep your pride and joy safe and dry. Our brand new, weatherproof containers
                are perfect for classic and vintage car storage with easy drive-up access.
              </p>
            </div>

            <div className="ideal-card">
              <div className="ideal-icon icon-business"></div>
              <h3>Business Stock &amp; Inventory</h3>
              <p>
                Need extra space for growing stock levels? Local businesses use our
                containers as a cost-effective way to expand their storage without
                committing to larger premises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Access */}
      <section id="location">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Location &amp; Access</h2>
            <p className="section-subtitle">
              Conveniently located on the Fosseway, near Bourton-on-the-Water
            </p>
          </div>

          <div className="location-content">
            <div className="location-info">
              <div className="info-group">
                <h3><span className="info-icon icon-pin"></span>Address</h3>
                <p>
                  Cotswold Storage<br />
                  The Grafters<br />
                  Fosseway<br />
                  Bourton-on-the-Water<br />
                  Gloucestershire<br />
                  GL54 2HW
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Cotswold+Storage,+The+Grafters,+Bourton-on-the-Water,+Cheltenham+GL54+2HW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-link"
                >
                  Get Directions →
                </a>
              </div>

              <div className="info-group">
                <h3><span className="info-icon icon-clock"></span>Access Hours</h3>
                <p>
                  Monday – Friday: 8am – 5pm<br />
                  Saturday: 8am – 1pm<br />
                  Sunday: By appointment only
                </p>
                <p className="access-note">
                  Large HGV / removal company deliveries require 24 hours' notice.
                </p>
              </div>

              <div className="info-group">
                <h3><span className="info-icon icon-phone"></span>Get in Touch</h3>
                <p>
                  Phone: <a href="tel:01451822220" className="contact-link">01451 822220</a><br />
                  Find us on <a href="https://www.facebook.com/profile.php?id=61587056791212" target="_blank" rel="noopener noreferrer" className="contact-link">Facebook</a>
                </p>
              </div>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2461.764438830913!2d-1.7457050999999997!3d51.9017628!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48712591dea97dc5%3A0x40b4a2a0ae8f5c7b!2sCotswold%20Storage!5e0!3m2!1sen!2suk!4v1772367593699!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Cotswold Storage Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Get in Touch</h2>
            <p className="section-subtitle">
              Book a site visit or ask us anything — call us on{' '}
              <a href="tel:01451822220" className="hero-phone-link">01451 822220</a>{' '}
              or fill out the form below
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="containerType">Container Size *</label>
                <select
                  id="containerType"
                  name="containerType"
                  value={formData.containerType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a size</option>
                  <option value="20ft">20ft Container</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label htmlFor="storageType">What are you looking to store? *</label>
                <select
                  id="storageType"
                  name="storageType"
                  value={formData.storageType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a category</option>
                  <option value="household">Household / Personal Items</option>
                  <option value="classic-car">Classic Car / Vehicle</option>
                  <option value="business-stock">Business Stock / Inventory</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label htmlFor="message">Additional Details</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us a bit about what you need to store, or any questions you have..."
                ></textarea>
              </div>
            </div>

            <div className="turnstile-wrapper">
              {isFormReady ? (
                <Turnstile
                  ref={turnstileRef}
                  siteKey="0x4AAAAAACcjImr4V0G2g7DG"
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                    setSubmitError('');
                  }}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => {
                    setTurnstileToken(null);
                    setSubmitError('Verification failed. Please try again.');
                  }}
                  options={{
                    theme: 'light',
                    size: 'normal',
                  }}
                />
              ) : (
                <p className="turnstile-hint">Please complete all required fields to continue</p>
              )}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting || !turnstileToken}
            >
              {isSubmitting ? 'Sending...' : 'Send Enquiry'}
            </button>

            {submitError && (
              <div className="error-message">
                {submitError}
              </div>
            )}

            {submitted && (
              <div className="success-message">
                ✓ Thank you for your enquiry! We'll be in touch shortly to arrange your site visit.
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <p>Cotswold Storage is operated by Eco Traveler LTD</p>
          <p>The Grafters, Fosseway, Bourton-on-the-Water, Gloucestershire, GL54 2HW</p>
          <p className="footer-phone">
            <a href="tel:01451822220">01451 822220</a>
          </p>
          <p className="footer-copyright">&copy; 2026 Eco Traveler LTD. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
